import {
  TBaseHumanStepDefinition,
  TWorkflowDefinition,
  TWorkflowExecution,
  TWorkflowStepDefinition,
  WorkflowRuntimeStepStatus,
  WorkflowStepType,
} from '@commenda/types';

export type SLAStatus = 'on-track' | 'at-risk' | 'breached';

type WorkflowGraph = {
  nextStepIds: Map<string, string[]>;
  stepDefinitionsById: Map<string, TWorkflowStepDefinition>;
  startStepId: string | null;
};

function isSplitGateway(type: WorkflowStepType): boolean {
  return (
    type === WorkflowStepType.GATEWAY_PARALLEL_SPLIT ||
    type === WorkflowStepType.GATEWAY_BOOLEAN_EXCLUSIVE_SPLIT ||
    type === WorkflowStepType.GATEWAY_INCLUSIVE_SPLIT
  );
}

function isJoinGateway(type: WorkflowStepType): boolean {
  return type === WorkflowStepType.GATEWAY_PARALLEL_JOIN;
}

export function getStepEstimatedDurationMs(
  step: TWorkflowStepDefinition
): number {
  const humanStep = step as TBaseHumanStepDefinition;
  return humanStep.estimatedTimeToComplete?.durationMs ?? 0;
}

export function buildWorkflowGraph(
  definition: TWorkflowDefinition
): WorkflowGraph {
  const stepIdToNextStepIds = new Map<string, string[]>();
  const stepDefinitionsById = new Map<string, TWorkflowStepDefinition>();
  let startStepId: string | null = null;

  definition.steps.forEach((step) => {
    stepDefinitionsById.set(step.id, step);
    if (step.type === WorkflowStepType.START) {
      startStepId = step.id;
    }
    stepIdToNextStepIds.set(step.id, []);
  });

  definition.stepTransitions.forEach((transition) => {
    const existingNextStepIds =
      stepIdToNextStepIds.get(transition.fromStepId) || [];
    existingNextStepIds.push(transition.toStepId);
    stepIdToNextStepIds.set(transition.fromStepId, existingNextStepIds);
  });

  return {
    nextStepIds: stepIdToNextStepIds,
    stepDefinitionsById,
    startStepId,
  };
}

function requiresJoinGateway(type: WorkflowStepType): boolean {
  return (
    type === WorkflowStepType.GATEWAY_PARALLEL_SPLIT ||
    type === WorkflowStepType.GATEWAY_INCLUSIVE_SPLIT
  );
}

function findJoinGatewayForSplit(
  splitStepId: string,
  graph: WorkflowGraph
): string | null {
  const splitStepDefinition = graph.stepDefinitionsById.get(splitStepId);
  if (!splitStepDefinition) return null;
  if (!requiresJoinGateway(splitStepDefinition.type)) return null;

  const branchStartStepIds = graph.nextStepIds.get(splitStepId) || [];
  if (branchStartStepIds.length === 0) return null;

  const reachableJoinGatewaysPerBranch: Set<string>[] = [];

  branchStartStepIds.forEach((branchStartStepId) => {
    const reachableJoinGatewayIds = new Set<string>();
    const stepsToVisit = [branchStartStepId];
    const visitedStepIds = new Set<string>();

    while (stepsToVisit.length > 0) {
      const currentStepId = stepsToVisit.shift();
      if (currentStepId && !visitedStepIds.has(currentStepId)) {
        visitedStepIds.add(currentStepId);
        const currentStep = graph.stepDefinitionsById.get(currentStepId);
        if (currentStep) {
          if (isJoinGateway(currentStep.type)) {
            reachableJoinGatewayIds.add(currentStepId);
          }
          const nextStepIds = graph.nextStepIds.get(currentStepId) || [];
          nextStepIds.forEach((nextStepId) => {
            if (!visitedStepIds.has(nextStepId)) {
              stepsToVisit.push(nextStepId);
            }
          });
        }
      }
    }
    reachableJoinGatewaysPerBranch.push(reachableJoinGatewayIds);
  });

  if (reachableJoinGatewaysPerBranch.length === 0) return null;

  const joinGatewaysFromFirstBranch = reachableJoinGatewaysPerBranch[0];
  const joinGatewaysReachableFromAllBranches = Array.from(
    joinGatewaysFromFirstBranch
  ).filter((joinGatewayId) =>
    reachableJoinGatewaysPerBranch.every((branchJoins) =>
      branchJoins.has(joinGatewayId)
    )
  );

  return joinGatewaysReachableFromAllBranches.length > 0
    ? joinGatewaysReachableFromAllBranches[0]
    : null;
}

export function calculateTotalExpectedWorkflowSLAMs(
  definition: TWorkflowDefinition
): number {
  const graph = buildWorkflowGraph(definition);
  if (!graph.startStepId) return 0;

  const globallyVisitedStepIds = new Set<string>();

  const calculateBranchExpectedDurationMs = (
    currentStepId: string,
    branchVisitedStepIds: Set<string>,
    stopAtJoinGatewayId?: string | null
  ): number => {
    const currentStepDefinition = graph.stepDefinitionsById.get(currentStepId);
    if (branchVisitedStepIds.has(currentStepId)) return 0;
    if (!currentStepDefinition) return 0;
    if (stopAtJoinGatewayId && currentStepId === stopAtJoinGatewayId) return 0;
    if (currentStepDefinition.type === WorkflowStepType.END) return 0;

    branchVisitedStepIds.add(currentStepId);

    const outgoingStepIds = graph.nextStepIds.get(currentStepId) || [];
    const currentStepEstimatedDurationMs =
      getStepEstimatedDurationMs(currentStepDefinition);

    if (isSplitGateway(currentStepDefinition.type)) {
      if (outgoingStepIds.length === 0)
        return currentStepEstimatedDurationMs;

      const nestedJoinGatewayId = findJoinGatewayForSplit(
        currentStepId,
        graph
      );
      const branchExpectedDurationsMs = outgoingStepIds.map(
        (branchStartStepId) => {
          const nestedBranchVisitedStepIds = new Set(branchVisitedStepIds);
          return calculateBranchExpectedDurationMs(
            branchStartStepId,
            nestedBranchVisitedStepIds,
            nestedJoinGatewayId
          );
        }
      );
      const longestBranchDurationMs = Math.max(
        0,
        ...branchExpectedDurationsMs
      );

      if (nestedJoinGatewayId) {
        const durationAfterNestedJoinMs = calculateBranchExpectedDurationMs(
          nestedJoinGatewayId,
          branchVisitedStepIds,
          stopAtJoinGatewayId
        );
        return (
          currentStepEstimatedDurationMs +
          longestBranchDurationMs +
          durationAfterNestedJoinMs
        );
      }
      return currentStepEstimatedDurationMs + longestBranchDurationMs;
    }

    if (isJoinGateway(currentStepDefinition.type)) {
      if (outgoingStepIds.length === 0)
        return currentStepEstimatedDurationMs;
      const remainingPathDurationMs = calculateBranchExpectedDurationMs(
        outgoingStepIds[0],
        branchVisitedStepIds,
        stopAtJoinGatewayId
      );
      return currentStepEstimatedDurationMs + remainingPathDurationMs;
    }

    if (outgoingStepIds.length === 0)
      return currentStepEstimatedDurationMs;

    if (outgoingStepIds.length === 1) {
      const remainingPathDurationMs = calculateBranchExpectedDurationMs(
        outgoingStepIds[0],
        branchVisitedStepIds,
        stopAtJoinGatewayId
      );
      return currentStepEstimatedDurationMs + remainingPathDurationMs;
    }

    const nextPathDurationsMs = outgoingStepIds.map((nextStepId) =>
      calculateBranchExpectedDurationMs(
        nextStepId,
        branchVisitedStepIds,
        stopAtJoinGatewayId
      )
    );
    const longestNextPathDurationMs = Math.max(0, ...nextPathDurationsMs);
    return currentStepEstimatedDurationMs + longestNextPathDurationMs;
  };

  const calculateExpectedDurationFromStep = (
    currentStepId: string
  ): number => {
    const currentStepDefinition = graph.stepDefinitionsById.get(currentStepId);
    if (globallyVisitedStepIds.has(currentStepId)) return 0;
    if (!currentStepDefinition) return 0;
    if (currentStepDefinition.type === WorkflowStepType.END) return 0;

    globallyVisitedStepIds.add(currentStepId);

    const outgoingStepIds = graph.nextStepIds.get(currentStepId) || [];
    const currentStepEstimatedDurationMs =
      getStepEstimatedDurationMs(currentStepDefinition);

    if (isSplitGateway(currentStepDefinition.type)) {
      if (outgoingStepIds.length === 0)
        return currentStepEstimatedDurationMs;

      const joinGatewayId = findJoinGatewayForSplit(currentStepId, graph);
      const branchExpectedDurationsMs = outgoingStepIds.map(
        (branchStartStepId) => {
          const branchVisitedStepIds = new Set(globallyVisitedStepIds);
          return calculateBranchExpectedDurationMs(
            branchStartStepId,
            branchVisitedStepIds,
            joinGatewayId
          );
        }
      );
      const longestBranchDurationMs = Math.max(
        0,
        ...branchExpectedDurationsMs
      );

      if (joinGatewayId) {
        globallyVisitedStepIds.delete(joinGatewayId);
        const durationAfterJoinMs =
          calculateExpectedDurationFromStep(joinGatewayId);
        return (
          currentStepEstimatedDurationMs +
          longestBranchDurationMs +
          durationAfterJoinMs
        );
      }
      return currentStepEstimatedDurationMs + longestBranchDurationMs;
    }

    if (isJoinGateway(currentStepDefinition.type)) {
      if (outgoingStepIds.length === 0)
        return currentStepEstimatedDurationMs;
      const remainingPathDurationMs = calculateExpectedDurationFromStep(
        outgoingStepIds[0]
      );
      return currentStepEstimatedDurationMs + remainingPathDurationMs;
    }

    if (outgoingStepIds.length === 0)
      return currentStepEstimatedDurationMs;
    if (outgoingStepIds.length === 1) {
      const remainingPathDurationMs = calculateExpectedDurationFromStep(
        outgoingStepIds[0]
      );
      return currentStepEstimatedDurationMs + remainingPathDurationMs;
    }

    const nextPathDurationsMs = outgoingStepIds.map((nextStepId) =>
      calculateExpectedDurationFromStep(nextStepId)
    );
    const longestNextPathDurationMs = Math.max(0, ...nextPathDurationsMs);
    return currentStepEstimatedDurationMs + longestNextPathDurationMs;
  };

  return calculateExpectedDurationFromStep(graph.startStepId);
}

export function calculateStepGroupExpectedSLAMs(
  definition: TWorkflowDefinition,
  stepGroupId: string
): number {
  const stepGroup = definition.stepGroups?.find(
    (group) => group.id === stepGroupId
  );
  if (!stepGroup) return 0;

  const stepDefinitionsById = new Map(
    definition.steps.map((step) => [step.id, step])
  );

  return stepGroup.stepIds.reduce((totalMs, stepId) => {
    const step = stepDefinitionsById.get(stepId);
    return totalMs + (step ? getStepEstimatedDurationMs(step) : 0);
  }, 0);
}

/**
 * Compute total elapsed time across only SLA-configured steps.
 * For each step with estimatedTimeToComplete, sum elapsed time
 * (startedAt -> completedAt or now).
 */
export function calculateServiceElapsedSLAMs(
  workflowExecution: TWorkflowExecution
): number {
  const definition =
    workflowExecution.WorkflowTemplateVersion?.definition;
  const stepExecutions = workflowExecution.runtime?.stepExecutions;
  if (!definition || !stepExecutions) return 0;

  let totalElapsedMs = 0;

  for (const stepDef of definition.steps) {
    const estimatedMs = getStepEstimatedDurationMs(stepDef);
    if (estimatedMs <= 0) continue;

    const stepExec = Object.values(stepExecutions).find(
      (exec) => exec.stepDefinitionId === stepDef.id
    );
    if (!stepExec?.startedAt) continue;

    const startTime = new Date(stepExec.startedAt).getTime();
    if (
      stepExec.status === WorkflowRuntimeStepStatus.COMPLETED &&
      stepExec.completedAt
    ) {
      totalElapsedMs += new Date(stepExec.completedAt).getTime() - startTime;
    } else if (stepExec.status !== WorkflowRuntimeStepStatus.DISABLED) {
      totalElapsedMs += Date.now() - startTime;
    }
  }

  return totalElapsedMs;
}

/**
 * Compute SLA metrics for a single task/step.
 */
export function calculateTaskSLAMs(
  stepDefinition: TWorkflowStepDefinition,
  workflowExecution: TWorkflowExecution
): { expectedMs: number; elapsedMs: number } | null {
  const estimatedMs = getStepEstimatedDurationMs(stepDefinition);
  if (estimatedMs <= 0) return null;

  const stepExecutions = workflowExecution.runtime?.stepExecutions;
  if (!stepExecutions) return { expectedMs: estimatedMs, elapsedMs: 0 };

  const stepExec = Object.values(stepExecutions).find(
    (exec) => exec.stepDefinitionId === stepDefinition.id
  );
  if (!stepExec?.startedAt) return { expectedMs: estimatedMs, elapsedMs: 0 };

  const startTime = new Date(stepExec.startedAt).getTime();
  let elapsedMs = 0;

  if (
    stepExec.status === WorkflowRuntimeStepStatus.COMPLETED &&
    stepExec.completedAt
  ) {
    elapsedMs = new Date(stepExec.completedAt).getTime() - startTime;
  } else if (stepExec.status !== WorkflowRuntimeStepStatus.DISABLED) {
    elapsedMs = Date.now() - startTime;
  }

  return { expectedMs: estimatedMs, elapsedMs };
}

export function getSLAUtilization(
  elapsedMs: number,
  expectedMs: number
): number {
  if (expectedMs <= 0) return 0;
  return Math.round((elapsedMs / expectedMs) * 100);
}

export function getSLAStatus(
  elapsedMs: number,
  expectedMs: number
): SLAStatus {
  if (expectedMs <= 0) return 'on-track';
  const utilization = elapsedMs / expectedMs;
  if (utilization > 1) return 'breached';
  if (utilization >= 0.7) return 'at-risk';
  return 'on-track';
}

/**
 * Check if a workflow definition has any steps with SLA configured.
 */
export function workflowHasSLA(
  definition: TWorkflowDefinition | undefined
): boolean {
  if (!definition) return false;
  return definition.steps.some(
    (step) => getStepEstimatedDurationMs(step) > 0
  );
}
